import React from 'react';
import { Loader2 } from 'lucide-react';

interface IncentiveSummaryCardProps {
  productName?: string;
  incentiveRate?: string | number;
  unitThreshold?: string | number;
  unitType?: string;
  startDate?: string;
  city?: string;
  citiesList?: string[];
  onRemoveCity?: (city: string) => void;
  onCancel?: () => void;
  onSaveDraft?: () => void;
  onSubmit: (e?: React.FormEvent) => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
}

export const IncentiveSummaryCard: React.FC<IncentiveSummaryCardProps> = ({
  productName = '{Product Name}',
  incentiveRate = '1',
  unitThreshold = '1L',
  startDate = 'mm/dd/yyyy',
  city,
  citiesList,
  onRemoveCity,
  onCancel,
  onSaveDraft,
  onSubmit,
  isSubmitting = false,
  submitButtonText = 'Publish Incentive',
}) => {
  const formattedRate = incentiveRate ? `₹${incentiveRate}` : '₹1';
  const displayProd = productName && productName !== 'All Products' ? productName : '{Product Name}';
  const displayThreshold = unitThreshold || '1L';

  let citiesToDisplay: string[] = [];
  if (Array.isArray(citiesList) && citiesList.length > 0) {
    citiesToDisplay = citiesList;
  } else if (typeof city === 'string' && city.trim()) {
    citiesToDisplay = city.split(', ').map((s) => s.trim()).filter(Boolean);
  }

  return (
    <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm mt-6 select-none">
      <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">
        INCENTIVE SUMMARY
      </div>

      {/* Summary Box */}
      <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-4 mb-6 space-y-2">
        <div className="font-bold text-gray-900 text-sm md:text-base">
          {formattedRate} on purchase of {displayThreshold} {displayProd}
        </div>

        {/* Selected Cities Badges */}
        {citiesToDisplay.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <span className="text-xs font-semibold text-gray-500 mr-1">Cities:</span>
            {citiesToDisplay.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1 bg-red-50 text-[#B71C1C] border border-red-100 px-2 py-0.5 rounded-md text-xs font-semibold"
              >
                {c}
                {onRemoveCity && (
                  <button
                    type="button"
                    onClick={() => onRemoveCity(c)}
                    className="hover:text-red-800 text-red-400 ml-0.5 cursor-pointer font-bold"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        <div className="text-xs text-gray-500 font-medium pt-0.5">
          Start: {startDate || 'mm/dd/yyyy'}
        </div>
      </div>

      {/* Action Buttons matching Screenshot 2 */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <button
          type="button"
          onClick={onSaveDraft || onCancel}
          className="px-6 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs md:text-sm font-bold rounded-xl transition-all cursor-pointer shadow-2xs"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={() => onSubmit()}
          disabled={isSubmitting}
          className="px-6 py-2.5 bg-[#B71C1C] hover:bg-[#960101] text-white text-xs md:text-sm font-bold rounded-xl transition-all cursor-pointer shadow-xs flex items-center gap-2 disabled:opacity-50"
        >
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          <span>{submitButtonText}</span>
        </button>
      </div>
    </div>
  );
};
